import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ClipboardCheck,
  BrainCircuit,
  CalendarClock,
  ShieldCheck,
  BarChart3,
  Settings2,
  FileText,
  FolderSearch,
  FileSignature,
  Workflow,
  Code2,
  Compass,
  Sparkles,
  ScanSearch,
} from "lucide-react";
import { FLAGSHIP_PRODUCTS, CORE_SOLUTIONS } from "@/data/solutions";
import TiltCard from "@/components/fx/TiltCard";
import ScrambleText from "@/components/fx/ScrambleText";

const ICONS = {
  ScanSearch,
  ClipboardCheck,
  BrainCircuit,
  CalendarClock,
  ShieldCheck,
  BarChart3,
  Settings2,
  FileText,
  FolderSearch,
  FileSignature,
  Workflow,
  Code2,
  Compass,
};

export default function ServicesSection({ compact = false }) {
  return (
    <section
      data-testid="services-section"
      className="relative bg-white py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16">
          <div className="lg:col-span-6">
            <p className="font-mono-axis text-xs uppercase tracking-widest text-[#1E3A8A] mb-4">
              <ScrambleText text="/01 · Portfólio de soluções" />
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-semibold text-[#111827] leading-[1.05]">
              Produtos que transformam a{" "}
              <span className="text-shimmer">operação hospitalar.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 flex items-end">
            <p className="text-base md:text-lg text-gray-600 leading-relaxed">
              Somos uma healthtech especializada no ecossistema{" "}
              <span className="text-[#111827] font-medium">ERP Tasy</span>.
              Desenvolvemos produtos próprios e soluções sob medida que
              automatizam processos, aumentam a produtividade e entregam
              informação estratégica para a tomada de decisão.
            </p>
          </div>
        </div>

        {/* Flagship products */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {FLAGSHIP_PRODUCTS.map((p, i) => {
            const Icon = ICONS[p.icon] || Sparkles;
            return (
              <motion.article
                key={p.id}
                data-testid={`flagship-${p.id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{
                  duration: 0.7,
                  delay: 0.1 * i,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="h-full"
              >
                <TiltCard
                  max={6}
                  className={`group h-full bg-white border p-8 lg:p-10 transition-shadow duration-300 hover:shadow-[0_20px_60px_-20px_rgba(59,130,246,0.45)] ${
                    p.isNew ? "border-[#3B82F6]/60 ring-1 ring-[#3B82F6]/20" : "border-gray-200"
                  }`}
                >
                <span
                  aria-hidden
                  className="absolute top-0 left-0 h-[3px] w-16 bg-[#1E3A8A] transition-[width] duration-300 group-hover:w-full group-hover:neon-line"
                />
                {p.isNew && (
                  <span
                    data-testid={`flagship-${p.id}-new-badge`}
                    className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-2 py-1 bg-[#0B1220] text-[#22D3EE] font-mono-axis text-[10px] uppercase tracking-widest"
                  >
                    <span className="relative w-1.5 h-1.5 rounded-full bg-[#22D3EE] text-[#22D3EE] live-dot" />
                    Novo
                  </span>
                )}
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 bg-[#111827] text-white flex items-center justify-center group-hover:bg-[#1E3A8A] transition-colors">
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <span className="font-mono-axis text-xs uppercase tracking-widest text-gray-400">
                    Produto · {String(i + 1).padStart(2, "0")}
                  </span>
                </div>

                <h3 className="font-display text-2xl lg:text-3xl font-semibold tracking-tighter text-[#111827]">
                  {p.name}
                </h3>
                <p className="mt-2 text-sm font-mono-axis uppercase tracking-widest text-[#1E3A8A]">
                  {p.tagline}
                </p>

                <p className="mt-6 text-sm text-gray-600 leading-relaxed">
                  <span className="text-gray-400 font-mono-axis uppercase tracking-widest text-[10px] block mb-1">
                    Problema
                  </span>
                  {p.problem}
                </p>
                <p className="mt-4 text-sm text-[#111827] leading-relaxed">
                  <span className="text-gray-400 font-mono-axis uppercase tracking-widest text-[10px] block mb-1">
                    Valor entregue
                  </span>
                  {p.value}
                </p>
                </TiltCard>
              </motion.article>
            );
          })}
        </div>

        {/* Core solutions grid */}
        <div className="mt-20 lg:mt-24">
          <div className="flex items-end justify-between mb-10 gap-6">
            <div>
              <p className="font-mono-axis text-xs uppercase tracking-widest text-[#1E3A8A] mb-3">
                Soluções especializadas
              </p>
              <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tighter font-semibold text-[#111827] leading-[1.05] max-w-2xl">
                Ecossistema completo em torno do ERP Tasy.
              </h3>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border border-gray-200 border-r-0 border-b-0">
            {CORE_SOLUTIONS.map((s, i) => {
              const Icon = ICONS[s.icon] || Sparkles;
              return (
                <motion.div
                  key={s.id}
                  data-testid={`solution-card-${s.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{
                    duration: 0.5,
                    delay: 0.05 * i,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="group relative bg-white border-r border-b border-gray-200 p-7 lg:p-8 hover:bg-[#F9FAFB] transition-colors"
                >
                  <span
                    aria-hidden
                    className="absolute top-0 left-0 h-[2px] w-0 bg-[#1E3A8A] transition-[width] duration-300 group-hover:w-full"
                  />
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-10 h-10 bg-[#111827] text-white flex items-center justify-center group-hover:bg-[#1E3A8A] transition-colors">
                      <Icon className="w-4 h-4" strokeWidth={1.75} />
                    </div>
                    <span className="font-mono-axis text-xs text-gray-400">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h4 className="font-display text-base lg:text-lg font-medium text-[#111827] tracking-tight leading-snug">
                    {s.name}
                  </h4>
                  <p className="mt-3 text-sm text-gray-600 leading-relaxed line-clamp-3">
                    {s.value}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Custom development callout */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          data-testid="custom-callout"
          className="mt-16 lg:mt-20 relative overflow-hidden bg-[#0B1220] text-white"
        >
          <div className="grain absolute inset-0 opacity-60" aria-hidden />
          <div
            aria-hidden
            className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#3B82F6]/10 blur-3xl pointer-events-none"
          />
          <div
            aria-hidden
            className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#06B6D4]/10 blur-3xl pointer-events-none"
          />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center p-8 lg:p-14">
            <div className="lg:col-span-2 flex lg:justify-center">
              <div className="relative w-16 h-16 lg:w-20 lg:h-20 border-2 border-[#3B82F6] rotate-45 flex items-center justify-center">
                <Sparkles
                  className="w-6 h-6 lg:w-8 lg:h-8 text-[#3B82F6] -rotate-45"
                  strokeWidth={1.5}
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <p className="font-mono-axis text-xs uppercase tracking-widest text-[#06B6D4] mb-3">
                Desenvolvimento sob medida
              </p>
              <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tighter font-semibold leading-[1.1]">
                Não encontrou o que precisa?{" "}
                <span className="text-[#3B82F6]">
                  Customizamos tudo para sua realidade.
                </span>
              </h3>
              <p className="mt-4 text-base text-gray-400 leading-relaxed max-w-2xl">
                Além dos nossos produtos, desenvolvemos sistemas totalmente sob
                medida — do levantamento de requisitos ao go-live. Cada projeto
                respeita os fluxos, integrações e particularidades da sua
                instituição.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {[
                  "Integrações Tasy",
                  "APIs personalizadas",
                  "Automações específicas",
                  "Módulos internos",
                  "Fluxos assistenciais únicos",
                ].map((t) => (
                  <span
                    key={t}
                    className="text-[11px] px-3 py-1.5 border border-white/15 text-gray-300 font-mono-axis"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-3 flex lg:justify-end">
              <Link
                to="/contato"
                data-testid="custom-callout-cta"
                className="group inline-flex items-center gap-2 px-6 py-4 bg-[#3B82F6] text-white text-sm font-medium hover:bg-[#1E3A8A] transition-colors"
              >
                Conte sua necessidade
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
              </Link>
            </div>
          </div>
        </motion.div>

        {!compact && (
          <div className="mt-14 flex justify-start">
            <Link
              to="/solucoes"
              data-testid="services-cta"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#111827] border-b-2 border-[#1E3A8A] pb-1 hover:gap-3 transition-all"
            >
              Explorar portfólio completo
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
